/**
 * Starter menu templates for new nutrition clinics.
 *
 * These are ordinary `menuTemplates` payloads — meals and options a coach can
 * drop straight into a menu and then adjust. They are seeded into
 * `clinics/{clinicId}/menuTemplates` on request (see `seedNutritionMenuTemplates`
 * in firebase.js), never read from here at runtime, so a clinic owns and can
 * freely edit or delete its copies.
 *
 * NOTE ON PLACEMENT: these deliberately do NOT live in
 * `src/data/systemContent/templates/`. That directory holds treatment-documentation
 * templates, whose consumers render a `fields` array and have no type filter — a
 * menu payload dropped in there would appear in the treatment-template lists and
 * break them. Menus keep their own collection for exactly this reason.
 *
 * Macro values are PER 100 GRAMS, matching the food catalog. `sizeOptions` maps
 * a unit name to its weight in grams; every unit used here is in the curated
 * vocabulary in `src/utils/foodsNormalize.mjs`.
 */

/** Ingredient ids are minted at seed time; these are stable template-local keys. */
const ingredient = (foodName, macros, sizeOptions, amount, selectedAmount) => ({
  foodName,
  calories: macros.calories,
  protein: macros.protein,
  carbohydrates: macros.carbohydrates,
  fat: macros.fat,
  sizeOptions: sizeOptions || {},
  amount,
  selectedAmount,
  connectedIngredients: [],
});

const EGG = { calories: 143, protein: 12.6, carbohydrates: 0.7, fat: 9.5 };
const WHOLE_BREAD = { calories: 247, protein: 13, carbohydrates: 41, fat: 3.4 };
const COTTAGE_5 = { calories: 103, protein: 11, carbohydrates: 3.4, fat: 5 };
const OLIVE_OIL = { calories: 884, protein: 0, carbohydrates: 0, fat: 100 };
const CHICKEN_BREAST = { calories: 165, protein: 31, carbohydrates: 0, fat: 3.6 };
const WHITE_RICE = { calories: 130, protein: 2.7, carbohydrates: 28.2, fat: 0.3 };
const GREEK_YOGURT = { calories: 59, protein: 10, carbohydrates: 3.6, fat: 0.4 };
const BANANA = { calories: 89, protein: 1.1, carbohydrates: 22.8, fat: 0.3 };
const ALMONDS = { calories: 579, protein: 21.2, carbohydrates: 21.6, fat: 49.9 };
const TUNA_WATER = { calories: 116, protein: 25.5, carbohydrates: 0, fat: 0.8 };

/**
 * Starter templates. `kind` matches MENU_TEMPLATE_KINDS in firebase.js.
 * `clinicTypes` is carried for symmetry with the other system content and is
 * what the seeding helper checks before offering them.
 */
export const nutritionMenuTemplates = [
  {
    kind: "block",
    name: "ארוחת בוקר — ביצים וטוסט",
    clinicTypes: ["nutrition"],
    payload: {
      blockName: "ארוחת בוקר",
      comment: null,
      subBlocks: [
        {
          subBlockName: "אפשרות 1",
          ingredients: [
            ingredient("ביצה קשה", EGG, { "יחידה בינונית": 50 }, 2, "יחידה בינונית"),
            ingredient("לחם מחיטה מלאה", WHOLE_BREAD, { פרוסה: 30 }, 2, "פרוסה"),
            ingredient("שמן זית", OLIVE_OIL, { כפית: 5 }, 1, "כפית"),
          ],
        },
      ],
    },
  },
  {
    kind: "block",
    name: "ארוחת צהריים — עוף ואורז",
    clinicTypes: ["nutrition"],
    payload: {
      blockName: "ארוחת צהריים",
      comment: null,
      subBlocks: [
        {
          subBlockName: "אפשרות 1",
          ingredients: [
            ingredient("חזה עוף צלוי", CHICKEN_BREAST, {}, 150, "גרם"),
            ingredient("אורז לבן מבושל", WHITE_RICE, { כוס: 160 }, 1, "כוס"),
            ingredient("שמן זית", OLIVE_OIL, { כפית: 5 }, 1, "כפית"),
          ],
        },
      ],
    },
  },
  {
    kind: "block",
    name: "ארוחת ערב קלה",
    clinicTypes: ["nutrition"],
    payload: {
      blockName: "ארוחת ערב",
      comment: null,
      subBlocks: [
        {
          subBlockName: "אפשרות 1",
          ingredients: [
            ingredient("קוטג׳ 5%", COTTAGE_5, { גביע: 250 }, 1, "גביע"),
            ingredient("לחם מחיטה מלאה", WHOLE_BREAD, { פרוסה: 30 }, 1, "פרוסה"),
          ],
        },
      ],
    },
  },
  {
    kind: "subBlock",
    name: "ביניים — יוגורט ופרי",
    clinicTypes: ["nutrition"],
    payload: {
      subBlockName: "ארוחת ביניים",
      ingredients: [
        ingredient("יוגורט יווני 0%", GREEK_YOGURT, { גביע: 150 }, 1, "גביע"),
        ingredient("בננה", BANANA, { "יחידה בינונית": 118 }, 1, "יחידה בינונית"),
      ],
    },
  },
  {
    kind: "subBlock",
    name: "ביניים — שקדים",
    clinicTypes: ["nutrition"],
    payload: {
      subBlockName: "ארוחת ביניים",
      ingredients: [
        ingredient("שקדים", ALMONDS, { חופן: 28 }, 1, "חופן"),
      ],
    },
  },
  {
    kind: "food",
    name: "טונה במים",
    clinicTypes: ["nutrition"],
    payload: ingredient(
      "טונה במים",
      TUNA_WATER,
      { "קופסת שימורים": 160 },
      1,
      "קופסת שימורים"
    ),
  },
  {
    kind: "food",
    name: "חזה עוף צלוי",
    clinicTypes: ["nutrition"],
    payload: ingredient("חזה עוף צלוי", CHICKEN_BREAST, {}, 150, "גרם"),
  },
];

export default nutritionMenuTemplates;
